import { useEffect, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";

import { DisplayScreen } from "@client/features/display/display-screen";
import { DisplayGameSocket, type DisplayConnectionState } from "@client/realtime/display-socket";
import {
  DisplayCodeSchema,
  DisplayStateResponseSchema,
  ErrorResponseSchema,
  type DisplayProjection,
} from "@saboteur/contracts";

type RouteProblem = "invalid" | "expired" | "unavailable";

async function loadDisplayState(displayCode: string): Promise<DisplayProjection> {
  const response = await fetch(`/api/v1/displays/${encodeURIComponent(displayCode)}/state`, {
    headers: { Accept: "application/json" },
  });
  const body: unknown = await response.json().catch(() => undefined);
  if (!response.ok) {
    const parsed = ErrorResponseSchema.safeParse(body);
    if (parsed.success && parsed.data.code === "ROOM_EXPIRED") throw new Error("ROOM_EXPIRED");
    if (response.status === 404) throw new Error("ROOM_NOT_FOUND");
    throw new Error("DISPLAY_UNAVAILABLE");
  }
  return DisplayStateResponseSchema.parse(body).state;
}

function ProblemScreen({ problem }: { problem: RouteProblem }): ReactNode {
  const expired = problem === "expired";
  return (
    <main className="centered-page" data-audience="display">
      <section className="surface stack" role="alert">
        <p className="eyebrow">Shared display</p>
        <h1>{expired ? "This room has expired" : "Display room not found"}</h1>
        <p>
          {expired
            ? "Ask the host to create a new game room."
            : problem === "unavailable"
              ? "The display could not reconnect. Check the network and refresh."
              : "Check the TV link or display code and try again."}
        </p>
      </section>
    </main>
  );
}

export function DisplayRoute(): ReactNode {
  const { displayCode = "" } = useParams();
  const [projection, setProjection] = useState<DisplayProjection>();
  const [connectionState, setConnectionState] = useState<DisplayConnectionState>("connecting");
  const [problem, setProblem] = useState<RouteProblem>();

  useEffect(() => {
    const parsedCode = DisplayCodeSchema.safeParse(displayCode);
    if (!parsedCode.success) {
      setProblem("invalid");
      return;
    }

    let active = true;
    let socket: DisplayGameSocket | undefined;
    setProjection(undefined);
    setProblem(undefined);
    setConnectionState("connecting");
    void (async () => {
      let state: DisplayProjection | undefined;
      for (let attempt = 0; active && attempt < 6; attempt += 1) {
        try {
          state = await loadDisplayState(parsedCode.data);
          break;
        } catch (error) {
          if (!active) return;
          const message = error instanceof Error ? error.message : "";
          if (message === "ROOM_EXPIRED" || message === "ROOM_NOT_FOUND") {
            setProblem(message === "ROOM_EXPIRED" ? "expired" : "invalid");
            return;
          }
          if (attempt === 5) {
            setProblem("unavailable");
            return;
          }
          await new Promise((resolve) => window.setTimeout(resolve, 250 * 2 ** attempt));
        }
      }
      if (!active || !state) return;
      socket = new DisplayGameSocket({
        displayCode: parsedCode.data,
        initialVersion: state.version,
      });
      socket.subscribe(({ state: nextState }) => {
        if (active) setProjection(nextState);
      });
      socket.subscribeStatus((status) => {
        if (!active) return;
        setConnectionState(status);
        if (status === "expired") setProblem("expired");
        else if (status === "invalid") setProblem("invalid");
      });
      socket.acceptBootstrap(state);
      socket.connect();
    })();

    return () => {
      active = false;
      socket?.disconnect();
    };
  }, [displayCode]);

  if (problem) return <ProblemScreen problem={problem} />;
  if (projection) {
    return <DisplayScreen projection={projection} connectionState={connectionState} />;
  }
  return (
    <main className="centered-page" data-audience="display">
      <section className="surface stack" aria-live="polite">
        <p className="eyebrow">Shared display</p>
        <h1>Connecting to the mine…</h1>
      </section>
    </main>
  );
}
